import React, { useState } from 'react';
import CustomModel from '../../components/common/CustomModel';
import ConfirmationModal from '../../components/shareableContainer/ConfirmationModal';
import CustomSelect from '../../components/common/CustomSelect';
import { showErrorToast, showSuccessToast } from '../../components/Constant/Notification';
import { userUid } from '../../utils/storage';
import SittingService from '../Service/SittingService';

const SittingStatusModal = ({
  // Completion Modal Props
  isCompletedOpen,
  setIsCompletedOpen,
  
  // Cancellation Modal Props
  isCancelledOpen,
  setIsCancelledOpen,
  
  // Common Props
  selectedSittingData,
  onStatusUpdate, // Callback function to refresh data after status update
  
  // Optional customization props
  areaTypeOptions = [
    { value: "sqft", label: "SQ.FT" },
    { value: "cent", label: "Cent" },
    { value: "ground", label: "Ground" },
    { value: "acres", label: "Acres" },
  ]
}) => {
  // Completion form states
  const [areaType, setAreaType] = useState({ value: "sqft", label: "SQ.FT" });
  const [feedback, setFeedback] = useState("");
  const [negotiatedAmount, setNegotiatedAmount] = useState("");
  const [advanceAmount, setAdvanceAmount] = useState("");
  
  // Cancellation form states
  const [cancellationReason, setCancellationReason] = useState("");

  // Reset completion form
  const resetCompletionForm = () => {
    setAreaType({ value: "sqft", label: "SQ.FT" });
    setFeedback("");
    setNegotiatedAmount("");
    setAdvanceAmount("");
  };

  // Reset cancellation form
  const resetCancellationForm = () => {
    setCancellationReason("");
  };

  // Validate completion form
  const validateCompletionForm = () => {
    if (feedback.trim() === "") {
      showErrorToast("Feedback is required.");
      return false;
    }
    if (!negotiatedAmount || negotiatedAmount <= 0) {
      showErrorToast("Negotiated Amount should be greater than zero.");
      return false;
    }
    if (!negotiatedAmount || negotiatedAmount < 1 || negotiatedAmount > 999999999) {
        showErrorToast("Negotiated Amount should be between 1 and 999999999.");
        return false;
        }
    if (advanceAmount && advanceAmount < 0) {
      showErrorToast("Advance Amount cannot be negative.");
      return false;
    }
    return true;
  };

  // Validate cancellation form
  const validateCancellationForm = () => {
    if (!cancellationReason.trim()) {
      showErrorToast("Cancellation reason is required.");
      return false;
    }
    return true;
  };

  // Handle status update
  const handleUpdateSittingStatus = async (sittingId, status) => {
    try {
      let body = {};
      
      if (status === "CANCELLED") {
        if (!validateCancellationForm()) return;
        
        body = {
          sittingId: sittingId || "",
          status: status || "",
          modifiedById: userUid,
          cancellationReason: cancellationReason || ""
        };
      } else if (status === "COMPLETED") {
        if (!validateCompletionForm()) return;
        
        body = {
          sittingId: sittingId || "",
          status: status || "",
          modifiedById: userUid,
          areaType: areaType?.value || "sqft",
          negotiatedAmount: Number(negotiatedAmount) || 0,
          feedback: feedback || "",
          advanceAmount: Number(advanceAmount) || 0,
        };
      }

      const response = await SittingService.sittingUpdateStatus(body);
      
      if (response.data) {
        showSuccessToast("Sitting status updated successfully.");
        
        // Reset forms and close modals
        resetCompletionForm();
        resetCancellationForm();
        setIsCompletedOpen(false);
        setIsCancelledOpen(false);
        
        // Call the callback to refresh data
        if (onStatusUpdate) {
          onStatusUpdate();
        }
      }
    } catch (error) {
      console.error("Error updating sitting status:", error);
      showErrorToast("Failed to update sitting status.");
    }
  };

  return (
    <>
      {/* Completion Modal */}
      <CustomModel
        isOpen={isCompletedOpen}
        isClose={() => {
          setIsCompletedOpen(false);
          resetCompletionForm();
        }}
        title="Sitting Completion Feedback"
        content={
          <div className="">
            <p className="mb-2 text-sm text-gray-500">Rate Discussed in This sitting</p>
            <div className="flex flex-col md:flex-row justify-center items-center gap-4">
              <CustomSelect
                options={areaTypeOptions}
                value={areaType}
                onChange={(selectedOption) => setAreaType(selectedOption)}
                placeholder="Select Area Type"
                isClearable={false}
              />
              <input
                type="number"
                name="negotiatedAmount"
                value={negotiatedAmount}
                onKeyDown={(e) => {
                  if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
                    e.preventDefault();
                  }
                }}
                min={0}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => setNegotiatedAmount(e.target.value)}
                placeholder="Enter Negotiated Amount"
                className="px-3 py-2 input-focus w-full no-arrow"
              />
            </div>
            
            <p className="my-2 text-sm text-gray-500">Feedback</p>
            <textarea
              name="feedback"
              rows={4}
              placeholder="Enter Feedback"
              onChange={(e) => setFeedback(e.target.value)}
              value={feedback}
              className="w-full px-3 py-2 input-focus"
            />

            <p className="my-2 text-sm text-gray-500">Any Advance Amount Given?</p>
            <input
              type="number"
              name="advanceAmount"
              value={advanceAmount}
              min={0}
              onWheel={(e) => e.target.blur()}
              onChange={(e) => setAdvanceAmount(e.target.value)}
              placeholder="Enter Advance Amount"
              className="px-3 py-2 input-focus w-full no-arrow"
            />
            
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => {
                  setIsCompletedOpen(false);
                  resetCompletionForm();
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdateSittingStatus(selectedSittingData?.sittingId || selectedSittingData?.id, "COMPLETED");
                }}
                className="px-4 py-2 text-white rounded-md bg-customGreen hover:bg-green-600 transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        }
      />

      {/* Cancellation Modal */}
      <ConfirmationModal
        showModal={isCancelledOpen}
        onClose={() => {
          setIsCancelledOpen(false);
          resetCancellationForm();
        }}
        title="Cancellation Feedback"
        message="Are you sure you want to cancel this sitting?"
        additionalProp={false}
        content={
          <div className="text-start">
            <p className="mb-2 text-sm text-gray-500">Reason for cancellation</p>
            <textarea
              name="cancellationReason"
              rows={4}
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
              placeholder="Enter Reason"
              className="w-full px-3 py-2 input-focus"
            />
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={() => {
                  setIsCancelledOpen(false);
                  resetCancellationForm();
                }}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleUpdateSittingStatus(selectedSittingData?.sittingId ||  selectedSittingData?.id, "CANCELLED");
                }}
                className="px-4 py-2 text-white rounded-md bg-customGreen hover:bg-green-600 transition-colors"
              >
                Submit
              </button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default SittingStatusModal;